import { Badge } from "@/components/ui/badge";
import { AlertCircle, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useContext, useState } from "react";
import { DebugContext } from "@/routes/_authenticated/ava/$chatId";
import { ResultRenderer } from "./result-renderer";
import { InsightsTool } from "./insights-tools";
import { MentorshipTool } from "./mentorship-tools";
import { MessageTool } from "./message-tools";
import { ScoringTool } from "./scoring-tools";
import { SearchUsersTool } from "./search-users-tool";
import { SurveyTool } from "./survey-tools";

interface ToolDispatcherProps {
  toolName: string;
  args: any;
  result?: string;
  status?: "running" | "complete" | "error";
  _toolCallId?: string;
}

// Collapsible tool card wrapper - minimal and elegant with proper backgrounds
function CollapsibleToolCard({
  toolName,
  status,
  children,
}: {
  toolName: string;
  status?: string;
  children: React.ReactNode;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Auto-collapse when complete
  const shouldAutoCollapse = status === "complete";
  const isCollapsed = shouldAutoCollapse && !isExpanded;

  const formatToolName = (name: string) =>
    name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  const getStatusDisplay = () => {
    switch (status) {
      case "running":
        return <Loader2 className="h-3 w-3 animate-spin text-blue-500" />;
      case "complete":
        return <span className="text-xs text-green-600">✓</span>;
      case "error":
        return <AlertCircle className="h-3 w-3 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="mb-2 min-w-[600px] rounded-md border border-gray-200/50 bg-gray-50/30 text-sm">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center gap-2 p-3 text-left transition-colors hover:bg-gray-50/50"
      >
        <div className="flex h-3 w-3 items-center justify-center text-gray-400">
          {isCollapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </div>
        <span className="flex-1 text-gray-600">{formatToolName(toolName)}</span>
        {getStatusDisplay()}
      </button>

      {!isCollapsed && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

// Generic fallback tool for tools that don't have specific UIs
function GenericTool({ toolName, args, result, status }: ToolDispatcherProps) {
  return (
    <CollapsibleToolCard toolName={toolName} status={status}>
      {/* Show parameters */}
      {args && Object.keys(args).length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {Object.entries(args).map(([key, value]) => (
            <Badge key={key} variant="secondary" className="text-xs">
              {key}: {String(value)}
            </Badge>
          ))}
        </div>
      )}

      {/* Show result */}
      <ResultRenderer
        result={result}
        status={status || "complete"}
        fallbackMessage="Tool completed successfully"
      />
    </CollapsibleToolCard>
  );
}

export function ToolDispatcher({
  toolName,
  args,
  result,
  status,
  // _toolCallId,
}: ToolDispatcherProps) {
  const { isDebugMode } = useContext(DebugContext);

  // Extract status.type if status is an object (assistant-ui format)
  const extractedStatus =
    typeof status === "object" && status !== null && "type" in status
      ? (status as any).type
      : status;

  if (isDebugMode) {
    console.log("🎯 ToolDispatcher:", {
      toolName,
      status: extractedStatus,
      hasArgs: !!args,
      hasResult: !!result,
    });
  }

  // Route to specific tool UI components based on tool name
  switch (toolName) {
    // User Tools
    case "search_users":
      return (
        <CollapsibleToolCard toolName={toolName} status={extractedStatus}>
          <SearchUsersTool
            args={args}
            result={result}
            status={extractedStatus}
          />
        </CollapsibleToolCard>
      );

    case "get_user_profile":
    case "get_mentor_slots":
    case "get_all_mentor_slots":
      return (
        <GenericTool
          toolName={toolName}
          args={args}
          result={result}
          status={extractedStatus}
        />
      );

    // Memory & Survey Tools
    case "search_teen_memories":
    case "get_teen_memories":
    case "get_available_survey_types":
    case "get_recent_survey_summary":
    case "search_survey_summaries":
    case "search_surveys_by_type":
      return (
        <CollapsibleToolCard toolName={toolName} status={extractedStatus}>
          <SurveyTool
            toolName={toolName}
            args={args}
            result={result}
            status={extractedStatus}
          />
        </CollapsibleToolCard>
      );

    // Mentorship Tools
    case "get_mentorship_details":
    case "get_mentorships_by_teen":
    case "get_mentorships_by_mentor":
    case "get_mentorship_history_for_teen":
    case "get_all_mentorships_for_user":
      return (
        <CollapsibleToolCard toolName={toolName} status={extractedStatus}>
          <MentorshipTool
            toolName={toolName}
            args={args}
            result={result}
            status={extractedStatus}
          />
        </CollapsibleToolCard>
      );

    // Insights Tools
    case "get_teen_insights":
    case "get_insights_by_engagement_status":
    case "get_insights_needing_followup":
    case "get_recent_insights_for_mentorship":
    case "find_similar_insights":
      return (
        <CollapsibleToolCard toolName={toolName} status={extractedStatus}>
          <InsightsTool
            toolName={toolName}
            args={args}
            result={result}
            status={extractedStatus}
          />
        </CollapsibleToolCard>
      );

    // Scoring Tools
    case "get_mentor_score":
    case "compare_mentors":
    case "get_top_mentors_by_score":
    case "get_mentor_score_statistics":
    case "get_daily_average_retention_rates":
      return (
        <CollapsibleToolCard toolName={toolName} status={extractedStatus}>
          <ScoringTool
            toolName={toolName}
            args={args}
            result={result}
            status={extractedStatus}
          />
        </CollapsibleToolCard>
      );

    // Message & Communication Tools
    case "get_notes_by_mentorship":
    case "get_recent_messages":
    case "get_chat_history":
    case "search_messages_by_text":
    case "find_similar_messages":
    case "get_conversation_summary":
    case "get_messages_from_last_week":
      return (
        <CollapsibleToolCard toolName={toolName} status={extractedStatus}>
          <MessageTool
            toolName={toolName}
            args={args}
            result={result}
            status={extractedStatus}
          />
        </CollapsibleToolCard>
      );

    // Escalation Tools
    case "get_all_escalations":
    case "get_escalations_by_mentee":
    case "get_escalations_by_mentor":
      return (
        <CollapsibleToolCard toolName={toolName} status={extractedStatus}>
          <MessageTool
            toolName={toolName}
            args={args}
            result={result}
            status={extractedStatus}
          />
        </CollapsibleToolCard>
      );

    // Fallback for any unhandled tools - format name nicely
    default: {
      return (
        <CollapsibleToolCard toolName={toolName} status={extractedStatus}>
          <div className="text-sm text-gray-600">
            {args && Object.keys(args).length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {Object.entries(args).map(([key, value]) => (
                  <Badge key={key} variant="secondary" className="text-xs">
                    {key}: {String(value)}
                  </Badge>
                ))}
              </div>
            )}

            <ResultRenderer
              result={result}
              status={extractedStatus}
              fallbackMessage={`${toolName.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} completed successfully`}
            />
          </div>
        </CollapsibleToolCard>
      );
    }
  }
}
